import { createVirtualizer } from "@tanstack/solid-virtual";
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import { cx } from "../../../../styled-system/css";
import { QrModal, type QrModalTarget } from "../../../components/QrModal";
import { CoordDetailsButton } from "../../../components/CoordDetailsButton";
import { OwnershipCheckbox } from "../../../components/OwnershipCheckbox";
import type { QrDisplayItem } from "../../../components/QrList";
import { styles } from "../../../styles";
import { categoryDisplayName } from "../../../categories";
import { BrandChip } from "../../../components/BrandChip";
import { OwnershipSummary } from "../../../components/OwnershipSummary";
import type { ColorFilters, CoordEntry, CoordItem } from "../../../types";
import { buildCatalogListRows, hasCompleteEntryQr, isCatalogListRowOwned, type CatalogListRow } from "../selectors";
import { fullImageMatchesColor, itemMatchesColor } from "../../../search";

type Props = {
  entries: CoordEntry[];
  isSelected: (item: CoordItem) => boolean;
  onToggle: (item: CoordItem) => void;
  canUpdateOwnership: boolean;
  showItemQr: boolean;
  qrCycleIntervalMs: number;
  isOwnershipPending: (item: CoordItem) => boolean;
  onOwnershipChange: (item: CoordItem, owned: boolean) => void;
  onQrOpen: (item: CoordItem) => void;
  onPreviewEntry?: (entry: CoordEntry) => void;
  onScrollPositionChange?: (scrolled: boolean) => void;
  onBrandFilter?: (brand: string) => void;
  entryBrandOnly?: boolean;
  plainBrand?: boolean;
  toolbar?: JSX.Element;
  colorFilters?: ColorFilters;
};

function qrDisplayItem(row: CatalogListRow, entry: CoordEntry): QrDisplayItem {
  return {
    label: row.label,
    name: entry.name,
    id: row.id,
    qr: row.qr,
  };
}

export function CoordList(props: Props) {
  let scroller: HTMLDivElement | undefined;
  const [openKeys, setOpenKeys] = createSignal<ReadonlySet<string>>(new Set());
  const [qrTarget, setQrTarget] = createSignal<QrModalTarget | null>(null);
  const rowsByEntry = createMemo(() => new Map(props.entries.map((entry) => [entry.key, buildCatalogListRows(entry)])));
  const rowsFor = (entry: CoordEntry) => rowsByEntry().get(entry.key) ?? [];
  const isOpen = (key: string) => openKeys().has(key);
  const toggleOpen = (key: string) => setOpenKeys((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const rowSelected = (row: CatalogListRow) => row.items.some(props.isSelected);
  const rowMatchesColor = (row: CatalogListRow) => {
    const filters = props.colorFilters;
    return filters ? row.items.some((item) => itemMatchesColor(item, filters)) : false;
  };
  const showEntryQr = (entry: CoordEntry, rows: CatalogListRow[]) => setQrTarget({
    title: entry.name,
    items: rows.map((row) => qrDisplayItem(row, entry)),
  });
  type DisplayRow =
    | { kind: "parent"; entry: CoordEntry }
    | { kind: "item"; entry: CoordEntry; row: CatalogListRow };
  const displayRows = createMemo<DisplayRow[]>(() => props.entries.flatMap((entry) => [
    { kind: "parent" as const, entry },
    ...(isOpen(entry.key)
      ? rowsFor(entry).map((row) => ({ kind: "item" as const, entry, row }))
      : []),
  ]));
  const rowVirtualizer = createVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    get count() { return displayRows().length; },
    getScrollElement: () => scroller ?? null,
    estimateSize: () => 42,
    getItemKey: (index) => {
      const displayRow = displayRows()[index];
      return displayRow?.kind === "parent"
        ? `parent:${displayRow.entry.key}`
        : `item:${displayRow?.entry.key}:${displayRow?.row.key}`;
    },
    overscan: 10,
  });
  const virtualRows = () => rowVirtualizer.getVirtualItems();
  const paddingTop = () => virtualRows()[0]?.start ?? 0;
  const paddingBottom = () => {
    const last = virtualRows().at(-1);
    return last ? Math.max(0, rowVirtualizer.getTotalSize() - last.end) : 0;
  };
  const columnCount = () => 6 + (props.showItemQr ? 1 : 0) + (props.canUpdateOwnership ? 1 : 0);

  const renderDisplayRow = (displayRow: DisplayRow, index: number) => {
    const entry = displayRow.entry;
    if (displayRow.kind === "parent") {
      const rows = () => rowsFor(entry);
      return (
        <tr
          ref={(element) => rowVirtualizer.measureElement(element)}
          data-index={index}
          class={cx(
            styles.listParentRow,
            props.colorFilters && fullImageMatchesColor(entry, props.colorFilters) && styles.listColorMatched,
          )}
        >
          <td class={styles.listNameColumn}>
            <div class={styles.listNameContent}>
              <button
                class={styles.listExpandButton}
                type="button"
                onClick={() => toggleOpen(entry.key)}
                aria-expanded={isOpen(entry.key)}
                aria-label={`${entry.name}のパーツを${isOpen(entry.key) ? "閉じる" : "開く"}`}
              >
                <strong>{entry.name}</strong>
              </button>
              <Show when={entry.series === "primagi"}>
                <span class={styles.listAvailability}>使用{entry.available === true ? "可" : entry.available === false ? "不可" : "不明"}</span>
              </Show>
              <Show when={props.onPreviewEntry}>
                <CoordDetailsButton title={entry.name} onClick={() => props.onPreviewEntry?.(entry)} />
              </Show>
            </div>
          </td>
          <Show when={props.showItemQr}>
            <td>
              <Show when={hasCompleteEntryQr(rows())} fallback="-">
                <button class={styles.listQrButton} type="button" title="すべてのQRを表示" onClick={() => showEntryQr(entry, rows())}>ALL QR</button>
              </Show>
            </td>
          </Show>
          <Show when={props.canUpdateOwnership}>
            <td><OwnershipSummary items={entry.items} /></td>
          </Show>
          <td>{categoryDisplayName(entry.category)}</td>
          <td>{entry.star || "-"}</td>
          <td><BrandChip brand={entry.brand} onSelect={props.onBrandFilter} plain={props.plainBrand} /></td>
          <td>-</td>
          <td>-</td>
        </tr>
      );
    }

    const row = displayRow.row;
    return (
      <tr
        ref={(element) => rowVirtualizer.measureElement(element)}
        data-index={index}
        class={cx(
          styles.listItemRow,
          rowMatchesColor(row) && styles.listColorMatched,
          rowSelected(row) && styles.listItemSelected,
        )}
      >
        <td class={styles.listNameColumn}>
          <div class={styles.listItemNameContent}>
            <div class={styles.listCheckboxes}>
              <For each={row.items}>{(item) => (
                <input
                  type="checkbox"
                  checked={props.isSelected(item)}
                  title={`${item.term}を選択`}
                  aria-label={`${entry.name} ${item.term}を選択`}
                  onChange={() => props.onToggle(item)}
                />
              )}</For>
            </div>
            <span class={styles.listPartLabel}>{row.label}</span>
          </div>
        </td>
        <Show when={props.showItemQr}>
          <td>
            <button
              class={cx(styles.listQrButton, !row.qr && styles.listQrButtonMissing)}
              type="button"
              title={row.qr ? "QRを表示" : "QRを登録"}
              onClick={() => props.onQrOpen(row.items[0])}
            >
              {row.qr ? "QR" : "未"}
            </button>
          </td>
        </Show>
        <Show when={props.canUpdateOwnership}>
          <td>
            <OwnershipCheckbox
              checked={isCatalogListRowOwned(row)}
              disabled={row.items.some(props.isOwnershipPending)}
              label={`${entry.name} ${row.label}を${isCatalogListRowOwned(row) ? "未所持" : "所持済み"}に変更`}
              onChange={(owned) => props.onOwnershipChange(row.items[0], owned)}
            />
          </td>
        </Show>
        <td>{categoryDisplayName(row.category)}</td>
        <td>{entry.star || "-"}</td>
        <td><BrandChip brand={row.brand} onSelect={props.entryBrandOnly ? undefined : props.onBrandFilter} plain={props.plainBrand} /></td>
        <td>{row.point || "-"}</td>
        <td>{row.id || "-"}</td>
      </tr>
    );
  };

  return (
    <>
      <div
        ref={scroller}
        class={styles.listScroller}
        aria-live="polite"
        onScroll={(event) => {
          const scroller = event.currentTarget;
          const toolbar = scroller.querySelector<HTMLElement>("[data-view-toolbar]");
          props.onScrollPositionChange?.(toolbar
            ? toolbar.getBoundingClientRect().bottom <= scroller.getBoundingClientRect().top
            : scroller.scrollTop > 8);
        }}
      >
        {props.toolbar}
        <table class={cx(
          styles.coordList,
          !props.showItemQr && props.canUpdateOwnership && styles.coordListWithoutQr,
          !props.showItemQr && !props.canUpdateOwnership && styles.coordListWithoutStatus,
        )}>
          <thead>
            <tr>
              <th class={styles.listNameColumn}>コーデ・パーツ</th>
              <Show when={props.showItemQr}><th>QR</th></Show>
              <Show when={props.canUpdateOwnership}><th>所持</th></Show>
              <th>カテゴリ</th>
              <th>★</th>
              <th>ブランド</th>
              <th>ポイント</th>
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            <Show when={paddingTop() > 0}>
              <tr aria-hidden="true"><td colspan={columnCount()} style={{ height: `${paddingTop()}px`, padding: "0", border: "0" }} /></tr>
            </Show>
            <For each={virtualRows()}>{(virtualRow) => {
              const displayRow = () => displayRows()[virtualRow.index];
              // The virtualizer reuses each VirtualItem by index. Opening an
              // entry inserts rows in the middle, so the row at an existing
              // index can become a different catalog item. A keyed Show must
              // replace that <tr>; otherwise Solid keeps the previously
              // rendered row because both the old and new values are truthy.
              return (
                <Show when={displayRow()} keyed>
                  {(row) => renderDisplayRow(row, virtualRow.index)}
                </Show>
              );
            }}</For>
            <Show when={paddingBottom() > 0}>
              <tr aria-hidden="true"><td colspan={columnCount()} style={{ height: `${paddingBottom()}px`, padding: "0", border: "0" }} /></tr>
            </Show>
          </tbody>
        </table>
      </div>
      <QrModal target={qrTarget()} cycleIntervalMs={props.qrCycleIntervalMs} onClose={() => setQrTarget(null)} />
    </>
  );
}
